/**
 * Dashboard Page
 *
 * Main audit logs view with filtering, search, and log display.
 * Table data and count cards load in parallel — table renders first.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { Activity, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AuditLogFilters } from '@/components/audit-log-filters';
import { AuditLogsTable } from '@/components/audit-logs-table';
import { PaginationControls } from '@/components/pagination-controls';
import { AuditLogTabs } from '@/components/audit-log-tabs';
import { useAuditLogs, useAuditLogSummary } from '@/hooks/use-audit-logs';
import { useFilters } from '@/state/filters-context';
import { getCategoriesForTab, type AuditLogTab } from '@/types/audit-log';

export function DashboardPage() {
  const { filters, updateFilter } = useFilters();
  const [searchParams] = useSearchParams();

  const activeTab = (searchParams.get('tab') as AuditLogTab) || 'application';

  useEffect(() => {
    const categoriesForTab = getCategoriesForTab(activeTab);
    if (JSON.stringify(filters.category) !== JSON.stringify(categoriesForTab)) {
      updateFilter('category', categoriesForTab);
    }
  }, [activeTab, updateFilter, filters.category]);

  // These fire in parallel — table renders as soon as rows arrive
  const { data, isLoading, error, refetch } = useAuditLogs(filters);
  const { data: summary, isLoading: isSummaryLoading } = useAuditLogSummary(filters);

  const handlePageChange = (page: number) => updateFilter('page', page);
  const handleLimitChange = (limit: number) => updateFilter('limit', limit);

  const logsOnPage = data?.data.length || 0;
  const currentPage = data?.pagination.page || filters.page || 1;

  const criticalCount = summary?.severityCounts.critical ?? 0;
  const errorCount = summary?.severityCounts.error ?? 0;
  const totalLogs = summary?.total ?? 0;
  const isTotalCapped = summary?.isCapped ?? false;
  // A capped summary counted only the first 10,000 matched rows, so the
  // severity cards are floors. Say so rather than implying full coverage.
  const countScopeLabel = isTotalCapped
    ? 'In the first 10,000 matched'
    : 'Across all filtered results';
  // When capped, navigation intentionally stops at the cap (page 400 at 25/page) —
  // rows beyond 10,000 are reachable by narrowing filters, not by paging deeper.
  const totalPages = summary ? Math.ceil(summary.total / (filters.limit ?? 25)) : 1;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Audit Logs Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Monitor and analyze system activity, user actions, and security events
        </p>
      </div>

      <AuditLogTabs />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logs</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isSummaryLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-2xl font-bold">
                {totalLogs.toLocaleString()}
                {isTotalCapped && '+'}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">{logsOnPage} on current page</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Page</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentPage}</div>
            {isSummaryLoading ? (
              <Skeleton className="h-5 w-24 mt-1" />
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                of {totalPages}
                {isTotalCapped && '+'} pages
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Issues</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {isSummaryLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-red-600">
                {criticalCount.toLocaleString()}
                {isTotalCapped && '+'}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">{countScopeLabel}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Errors</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            {isSummaryLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {errorCount.toLocaleString()}
                {isTotalCapped && '+'}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">{countScopeLabel}</p>
          </CardContent>
        </Card>
      </div>

      {!isSummaryLoading && criticalCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">
              {criticalCount.toLocaleString()}
              {isTotalCapped && '+'} critical event(s)
            </span>{' '}
            found in {isTotalCapped ? 'the first 10,000 matched results' : 'filtered results'}.
            Review immediately.
          </AlertDescription>
        </Alert>
      )}

      <AuditLogFilters />

      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>
            Real-time system activity and user actions. Click any row for details.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <AuditLogsTable
            logs={data?.data || []}
            isLoading={isLoading}
            error={error}
            onRetry={() => refetch()}
          />
        </CardContent>
      </Card>

      {data && data.data.length > 0 && (
        <PaginationControls
          pagination={{
            total: summary?.total ?? 0,
            page: currentPage,
            limit: filters.limit ?? 25,
            totalPages,
          }}
          isTotalCapped={isTotalCapped}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
